"use client";

import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  toggleCategoryFollow,
  isFollowingCategory,
} from "@/app/actions/category/follow";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useRouter } from "next/navigation";

interface CategoryFollowButtonProps {
  categoryId: string;
  categoryName: string;
  userId?: string;
  size?: "default" | "sm" | "lg" | "icon";
  variant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";
}

export default function CategoryFollowButton({
  categoryId,
  categoryName,
  userId,
  size = "default",
  variant = "outline",
}: CategoryFollowButtonProps) {
  const [isFollowing, setIsFollowing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const router = useRouter();

  useEffect(() => {
    const checkFollowStatus = async () => {
      if (!userId) {
        setIsChecking(false);
        return;
      }
      setIsChecking(true);
      const following = await isFollowingCategory(userId, categoryId);
      setIsFollowing(following);
      setIsChecking(false);
    };

    checkFollowStatus();
  }, [userId, categoryId]);

  const handleFollowToggle = async () => {
    if (!userId) {
      toast.error("Please sign in to follow categories", {
        action: {
          label: "Sign In",
          onClick: () => router.push("/login"),
        },
      });
      return;
    }

    setIsLoading(true);
    const result = await toggleCategoryFollow(userId, categoryId);
    setIsLoading(false);

    if (result.success) {
      setIsFollowing(result.isFollowing);

      if (result.isFollowing) {
        toast.success(`Following ${categoryName}`);
      } else {
        toast.success(`Unfollowed ${categoryName}`);
      }
    } else {
      toast.error(result.message || "Failed to update follow status");
    }
  };

  if (!userId) {
    return null;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={isFollowing ? "following" : "not-following"}
        initial={{ opacity: 0.8, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0.8, scale: 0.95 }}
        transition={{ duration: 0.15 }}
      >
        <Button
          variant={isFollowing ? "secondary" : variant}
          size={size}
          onClick={handleFollowToggle}
          disabled={isLoading || isChecking}
          className={`gap-2 transition-all ${
            isFollowing ? "bg-primary/10 hover:bg-primary/20" : ""
          }`}
        >
          <Heart
            className={`h-4 w-4 transition-all ${
              isFollowing
                ? "fill-primary text-primary"
                : "text-muted-foreground"
            }`}
          />
          {isFollowing ? "Following" : "Follow"}
        </Button>
      </motion.div>
    </AnimatePresence>
  );
}
